import { request } from '@/lib/api'
import { asClearableString, makeDomainSave } from './domainSave'

/**
 * Доступ к доске и карточке сделки (тикет #71, ТЗ §4.2).
 *
 * ## Почему отдельно от `card/api.ts` и от `lists/api.ts`
 *
 * `card/api.ts` — универсальный слой каркаса #66: одна запись через механизм списков,
 * без знания о том, какая это сущность. `lists/api.ts` — плоские строки таблицы. Доска не
 * то и не другое: её колонки — это **стадии воронки**, которых в `crm.deal` нет вовсе, а
 * подытоги и признак застоя считает сервер. Складывать это в каркас значило бы, что
 * карточка товара начинает знать про воронки.
 *
 * ## Что читается отсюда, а что через каркас
 *
 * Поля самой сделки (название, сумма, срок) читает и правит каркас через `/lists`: там уже
 * работают полевые права и область видимости. Здесь — строки со скидками, история стадий
 * и доска, то есть ровно то, чего плоская строка дать не может.
 */

/** Стадия воронки: она же колонка доски. */
export interface FunnelStage {
  id: string
  code: string
  name: string
  stageOrder: number
  probability: number
  /** `open` | `won` | `lost`. Терминальная стадия закрывает сделку. */
  stageType: string
  /**
   * Лимит времени в стадии.
   *
   * `null` — «лимита нет», а не ноль: у терминальной стадии сделка стоит вечно по
   * определению, и ноль означал бы, что она застряла в тот же миг, как её выиграли.
   */
  maxDaysInStage: number | null
  color: string | null
}

/** Воронка со стадиями. Их несколько параллельно — B2B, повторные, что ещё заведут. */
export interface Funnel {
  id: string
  code: string
  name: string
  description: string | null
  defaultFunnel: boolean
  stages: FunnelStage[]
}

/**
 * Фактор прогноза (#128): какой признак, его значение у этой сделки и вклад в логарифм шансов
 * относительно средней сделки обучающей выборки. `effect` — поднимает (`up`) или опускает
 * (`down`) прогноз. Значения нет (`value` отсутствует), когда его нет и у сделки: например,
 * активностей не было ни разу.
 */
export interface ForecastFactor {
  code: string
  value?: number | string | null
  contribution: number
  effect: 'up' | 'down'
}

/**
 * Прогноз модели **рядом** со статической вероятностью стадии, а не вместо неё.
 *
 * `probability` — в процентах, как и вероятность стадии рядом. Поле отсутствует целиком,
 * когда прогноза нет (модель не обучена, мало данных, ещё не посчитан): это «нет прогноза»,
 * а не ноль, и экран обязан сказать это словами.
 */
export interface DealForecast {
  probability: number
  computedAt: string
  modelId: string
  factors: ForecastFactor[]
}

/** Состояние модели: обучена, отказалась обучаться (мало данных) или ещё не обучалась. */
export type ForecastStatus = 'trained' | 'insufficient_data' | 'none'

/**
 * Карточка на доске.
 *
 * `stuck` приходит **с сервера**: тот же признак считает отчёт о застое, и вторая
 * реализация на клиенте разошлась бы с ним ровно там, где на подсветку смотрят.
 */
export interface BoardCard {
  id: string
  number: string
  title: string
  amount: number
  currency: string
  probability: number
  weightedAmount: number
  stageId: string
  stageCode: string
  status: string
  counterpartyId: string | null
  counterpartyName: string | null
  responsibleEmployeeId: string | null
  expectedCloseOn: string | null
  stageChangedAt: string
  daysInStage: number
  maxDaysInStage: number | null
  stuck: boolean
  /** Прогноз модели (#128); нет — прогноза нет, а не ноль. */
  forecast?: DealForecast | null
}

/** Итог колонки в одной валюте: рубли с евро не складываются. */
export interface CurrencyTotal {
  currency: string
  count: number
  amount: number
  weightedAmount: number
  /** Сколько сделок этой валюты в колонке с прогнозом (#128). */
  forecastCount?: number
  /** Сумма × прогноз — только когда прогноз есть у каждой сделки этой валюты. */
  forecastWeightedAmount?: number | null
}

/** Сумма в одной валюте. */
export interface CurrencyAmount {
  currency: string
  amount: number
}

/**
 * Колонка доски.
 *
 * `count`, `amount` и `totals` — по **всей** колонке (агрегат на сервере в области
 * видимости), а `cards` — только первая порция. Это разные числа, и шапка колонки обязана
 * брать первые: «сделок: 30» над колонкой из восьмисот — ложь о воронке.
 *
 * `amount`/`weightedAmount` приходят одной цифрой только для одновалютной колонки; при
 * смеси валют их нет, и итоги читаются из `totals`.
 */
export interface BoardColumn {
  stageId: string
  stageCode: string
  stageName: string
  stageOrder: number
  probability: number
  stageType: string
  maxDaysInStage: number | null
  color: string | null
  count: number
  amount?: number | null
  /** Взвешенная сумма: «сколько может прийти» и «на что планировать» — разные вопросы. */
  weightedAmount?: number | null
  /**
   * Взвешенная по прогнозу (#128) — рядом с взвешенной по стадии. Одна цифра только для
   * одновалютной колонки, где прогноз есть у каждой сделки: сумма по части колонки читалась
   * бы как сумма колонки.
   */
  forecastWeightedAmount?: number | null
  /** Сколько сделок колонки с прогнозом. */
  forecastCount?: number
  totals: CurrencyTotal[]
  /** Сколько карточек колонки ещё не пришло. */
  remaining: number
  /** Курсор следующей порции; нет — колонка пришла целиком. */
  nextCursor?: string | null
  cards: BoardCard[]
}

export interface Board {
  funnelId: string
  funnelCode: string
  funnelName: string
  /** Одна цифра только для одной валюты; при смеси — `weightedTotals`. */
  weightedPipeline?: number | null
  weightedTotals: CurrencyAmount[]
  /** Состояние модели прогноза (#128) — чтобы сказать словами, почему прогноза нет. */
  forecastStatus?: ForecastStatus
  /** Воронка по прогнозу одной цифрой — только одна валюта и прогноз у каждой сделки. */
  forecastWeightedPipeline?: number | null
  /** По прогнозу по валютам — только валюты, где прогноз есть у каждой сделки. */
  forecastWeightedTotals?: CurrencyAmount[]
  forecastCount?: number
  columns: BoardColumn[]
}

/** Порция карточек колонки и курсор следующей. */
export interface CardPage {
  cards: BoardCard[]
  nextCursor?: string | null
}

/**
 * Строка сделки.
 *
 * `unitCost === null` значимо и не равно нулю: у услуги себестоимости может не быть, у
 * товара она может быть не заполнена, а ноль означал бы стопроцентную маржу на том, что
 * никто не мерил.
 */
export interface DealLine {
  id: string
  productId: string
  productSku: string
  productName: string
  quantity: number
  unitPrice: number
  discountPercent: number
  vatRate: number
  vatAmount: number
  lineTotal: number
  unitCost: number | null
  lineCost: number | null
  lineOrder: number
}

/**
 * Карточка сделки целиком.
 *
 * `marginAmount === null` — «неизвестна», а не «ноль»: одна строка без себестоимости
 * обнуляет маржу всей сделки, потому что частичная её завышает.
 */
export interface DealCard {
  id: string
  number: string
  title: string
  status: string
  amount: number
  currency: string
  probability: number
  weightedAmount: number
  funnelId: string
  funnelCode: string
  funnelName: string
  stageId: string
  stageCode: string
  stageName: string
  stageOrder: number
  stageType: string
  counterpartyId: string | null
  counterpartyName: string | null
  contactId: string | null
  contractId: string | null
  leadId: string | null
  responsibleEmployeeId: string | null
  departmentId: string | null
  territoryId: string | null
  expectedCloseOn: string | null
  closedAt: string | null
  stageChangedAt: string
  daysInStage: number
  maxDaysInStage: number | null
  stuck: boolean
  /** Комментарий к проигрышу; сама причина — `lossReasonId` из справочника (#100). */
  lossReason: string | null
  lossReasonId: string | null
  winReasonId: string | null
  version: number
  costAmount: number | null
  marginAmount: number | null
  lines: DealLine[]
  /** Прогноз модели (#128); нет — прогноза нет. */
  forecast?: DealForecast | null
  /** Состояние модели — почему прогноза нет, словами. */
  forecastStatus?: ForecastStatus
}

/**
 * Строка истории стадий.
 *
 * `backwards` — то, ради чего вкладка существует: показать только последнюю стадию значит
 * потерять, что сделка возвращалась. Считает его сервер сравнением порядка стадий —
 * клиент для этого держал бы свою карту воронки, и две разошлись бы молча.
 *
 * `fromStageId === null` у первой строки: сделку завели, возвращаться было неоткуда.
 */
export interface StageHistoryEntry {
  id: string
  fromStageId: string | null
  fromStageCode: string | null
  fromStageName: string | null
  toStageId: string
  toStageCode: string
  toStageName: string
  toStageType: string
  changedAt: string
  changedBy: string | null
  changedByName: string | null
  /** Время в **покинутой** стадии; у первой строки его нет. */
  durationSeconds: number | null
  backwards: boolean
}

const BASE = '/sales'

export function fetchFunnels(signal?: AbortSignal): Promise<Funnel[]> {
  return request<Funnel[]>(`${BASE}/funnels`, { signal })
}

export function fetchBoard(funnelId: string, signal?: AbortSignal): Promise<Board> {
  return request<Board>(`${BASE}/funnels/${funnelId}/board`, { signal })
}

/**
 * Следующая порция карточек одной колонки — кнопка «Показать ещё».
 *
 * Курсор берётся из ответа сервера и не собирается здесь: он привязан к колонке и к
 * порядку сортировки, и собранный руками молча пропустил бы или повторил карточки.
 */
export function fetchColumnCards(
  funnelId: string,
  stageId: string,
  after: string,
  signal?: AbortSignal,
): Promise<CardPage> {
  const query = new URLSearchParams({ after })
  return request<CardPage>(
    `${BASE}/funnels/${funnelId}/board/stages/${stageId}/cards?${query.toString()}`,
    { signal },
  )
}

export function fetchDealCard(dealId: string, signal?: AbortSignal): Promise<DealCard> {
  return request<DealCard>(`${BASE}/deals/${dealId}/card`, { signal })
}

export function fetchStageHistory(
  dealId: string,
  signal?: AbortSignal,
): Promise<StageHistoryEntry[]> {
  return request<StageHistoryEntry[]>(`${BASE}/deals/${dealId}/stage-history`, { signal })
}

/**
 * Переносит сделку в другую стадию.
 *
 * Зовёт тот же доменный метод, что и REST #67, а не свой UPDATE: в нём живут запись
 * истории, пересчёт маржи на выигрыше, публикация события исхода и запуск автоматизации.
 * Перенос, сделанный в обход, дал бы доску, на которой карточка двигается, а всё
 * перечисленное молчит.
 *
 * Причина проигрыша (`reasonId` из справочника, #100) обязательна для проигрышной стадии —
 * это правило домена, и клиент его не дублирует: сервер откажет кодом
 * `deal.loss_reason_required`, а вторая проверка здесь разошлась бы с первой при первой же
 * правке. `note` — необязательный комментарий к проигрышу.
 */
export function moveDealToStage(
  dealId: string,
  stageCode: string,
  reasonId?: string | null,
  note?: string | null,
): Promise<BoardCard> {
  return request<BoardCard>(`${BASE}/board/deals/${dealId}/stage`, {
    method: 'POST',
    body: { stageCode, reasonId: reasonId ?? null, lossReason: note ?? null },
  })
}

/** Причина закрытия сделки из справочника (#100). */
export interface DealReason {
  id: string
  kind: 'lost' | 'won'
  code: string
  name: string
  active: boolean
}

export function fetchDealReasons(kind: 'lost' | 'won', signal?: AbortSignal): Promise<DealReason[]> {
  return request<{ reasons: DealReason[] }>(`${BASE}/deal-reasons?kind=${kind}`, { signal }).then(
    (r) => r.reasons,
  )
}

export interface NewDeal {
  title: string
  funnelId: string
  counterpartyId?: string | null
  currency?: string | null
  expectedCloseOn?: string | null
  responsibleEmployeeId?: string | null
}

export function createDeal(body: NewDeal): Promise<{ id: string }> {
  return request<{ id: string }>(`${BASE}/deals`, { method: 'POST', body })
}

/** Позиция: без цены сервер берёт её из прайс-листа клиента в валюте сделки. */
export interface DealLineInput {
  productId?: string
  quantity?: number
  unitPrice?: number | null
  discountPercent?: number
  vatRate?: number
}

export function addDealLine(dealId: string, body: DealLineInput): Promise<DealCard> {
  return request<DealCard>(`${BASE}/deals/${dealId}/lines`, { method: 'POST', body })
}

export function updateDealLine(dealId: string, lineId: string, body: DealLineInput): Promise<DealCard> {
  return request<DealCard>(`${BASE}/deals/${dealId}/lines/${lineId}`, { method: 'PUT', body })
}

/** Удаляет открытую сделку без связанных документов; закрытую сервер не удаляет (#100). */
export function deleteDeal(dealId: string): Promise<void> {
  return request<void>(`${BASE}/deals/${dealId}`, { method: 'DELETE' })
}

export function removeDealLine(dealId: string, lineId: string): Promise<DealCard> {
  return request<DealCard>(`${BASE}/deals/${dealId}/lines/${lineId}`, { method: 'DELETE' })
}

/**
 * Сохранение полей сделки одним доменным PUT с версией (#100).
 *
 * Стадии, суммы и статуса здесь нет: стадию меняет перенос (история, события,
 * автоматизация), сумму — позиции, статус — закрытие. Их колонки на карточке только
 * показываются (`readOnlyColumns`).
 */
export const saveDeal = makeDomainSave((id) => `${BASE}/deals/${id}`, {
  title: { field: 'title', convert: asClearableString },
  counterparty_id: 'counterpartyId',
  currency: 'currency',
  expected_close_on: { field: 'expectedCloseOn', convert: asClearableString },
  responsible_employee_id: 'responsibleEmployeeId',
})

/**
 * Проигрышная ли стадия.
 *
 * Отдельной функцией, потому что от неё зависит, спрашивать ли причину отказа **до**
 * отправки. Спросить после отказа сервера тоже можно, но тогда перенос мышью выглядит
 * сломанным: карточка вернулась на место, а почему — написано мелким текстом.
 */
export function isLosingStage(stage: { stageType: string }): boolean {
  return stage.stageType === 'lost'
}

/** Коммерческое предложение сделки — как его отдаёт `/sales/deals/{id}/quotes`. */
export interface DealQuote {
  id: string
  number: string
  /** `draft` | `sent` | `accepted` | `rejected` | `expired`. */
  status: string
  dealId: string | null
  currency: string
  issuedOn: string | null
  validUntil: string | null
  total: number
  sentAt: string | null
}

/** КП сделки, новые первыми (право `sales.quote:read`). */
export function fetchDealQuotes(dealId: string, signal?: AbortSignal): Promise<DealQuote[]> {
  return request<DealQuote[]>(`${BASE}/deals/${dealId}/quotes`, { signal })
}
